import { useLengthConversion } from "hooks/useUnitConversion";

import { resolveShapefileBufferUnit } from "../util/shapefileBufferUnit";
import {
  selectRememberedShapefileBufferUnit,
  useShapefileBufferUnitStore
} from "../util/shapefileBufferUnitStore";
import { ShapefileBufferLabelUnit } from "../util/shapefileFilterPredicate";

/**
 * The unit a shapefile buffer is spelled in for whoever is looking at the
 * screen. Everywhere a boundary predicate is put into words — the chip and the
 * filter tree — reads it here and hands it to the label, which stays a plain
 * function of what it is given.
 *
 * It is the unit the buffer control's switch is set to, remembered per
 * browser, so a distance asked for in metres is read back in metres. A
 * remembered unit belonging to the other pair is ignored, and the pair's
 * larger unit answers instead: somebody now reading lengths in feet is not
 * being told about kilometres. Kilometres until the converter says otherwise —
 * it answers metres for everyone while its engine is loading, and a metric
 * reader is the buffer control's own default, so a label never flips out of
 * imperial on arrival.
 *
 * The remembered choice is subscribed to, not read off localStorage, so a flip
 * of the switch re-renders every label that names the unit. It used to be read
 * where it was used, on the grounds that flipping the switch re-committed the
 * buffer onto the predicate and so rebuilt the chips; staging took that write
 * away (EVA-206) and the subscription is what replaces it.
 */
export function useShapefileBufferLabelUnit(): ShapefileBufferLabelUnit {
  const { getDisplayUnit } = useLengthConversion();
  const remembered = useShapefileBufferUnitStore(selectRememberedShapefileBufferUnit);
  return resolveShapefileBufferUnit(getDisplayUnit(), remembered);
}

export default useShapefileBufferLabelUnit;
